import type { PromoCode, PromoCodeStatus, PromoRedemption } from '@manaro/contracts'
import { planLabel } from '@/features/salons/labels.ts'
import { formatDate, plural } from '@/shared/lib/format.ts'
import type { BadgeTone } from '@/shared/ui/badge.tsx'

type Terms = Pick<PromoCode, 'kind' | 'discountPercent' | 'discountPayments' | 'plan' | 'months'>

export const STATUS_LABELS: Record<PromoCodeStatus, string> = {
  active: 'Действует',
  inactive: 'Выключен',
  expired: 'Истёк',
  exhausted: 'Исчерпан',
}

export const STATUS_TONES: Record<PromoCodeStatus, BadgeTone> = {
  active: 'brand',
  inactive: 'neutral',
  expired: 'warn',
  exhausted: 'warn',
}

const SOURCE_LABELS: Record<string, string> = {
  registration: 'При регистрации',
  business_creation: 'При создании салона',
  billing: 'На странице тарифов',
}

export const sourceLabel = (source: string): string => SOURCE_LABELS[source] ?? source

const paymentsLabel = (count: number): string => `${count} ${plural(count, ['оплату', 'оплаты', 'оплат'])}`

export const monthsLabel = (months: number): string => `${months} ${plural(months, ['месяц', 'месяца', 'месяцев'])}`

/** The backend ends the chosen day at Kyiv midnight (21:59:59Z or 20:59:59Z), so the UTC date is still that day. */
export const backendDay = (iso: string): string => iso.slice(0, 10)

// A bare YYYY-MM-DD parses as UTC midnight; noon keeps the same day in any timezone.
const formatDay = (iso: string): string => formatDate(`${backendDay(iso)}T12:00:00`)

/** What the salon gets, in one line: «−20% на 1 оплату тарифа», «2 месяца Start бесплатно». */
export function promoTerms(terms: Terms): string {
  if (terms.kind === 'discount') {
    return `−${terms.discountPercent ?? 0}% на ${paymentsLabel(terms.discountPayments ?? 1)} тарифа`
  }
  return [monthsLabel(terms.months ?? 0), terms.plan && planLabel(terms.plan), 'бесплатно'].filter(Boolean).join(' ')
}

/** «3 из 50» with a limit, «3» without one. */
export const usageText = (promo: Pick<PromoCode, 'used' | 'maxRedemptions'>): string =>
  promo.maxRedemptions === null ? String(promo.used) : `${promo.used} из ${promo.maxRedemptions}`

export const expiryText = (promo: Pick<PromoCode, 'expiresAt'>): string =>
  promo.expiresAt ? `до ${formatDay(promo.expiresAt)}` : 'бессрочно'

/** What the code did for this salon, or why it has not done it yet. */
export function redemptionResult(redemption: PromoRedemption, promo: Terms): string {
  if (redemption.status === 'pending') return 'Ждёт, пока создадут салон'
  if (redemption.status === 'lapsed') return 'Не применён: салон не создали вовремя или код выключили'

  if (promo.kind === 'discount') {
    const left = redemption.paymentsLeft ?? 0
    return left > 0 ? `Осталось оплат со скидкой: ${left} из ${promo.discountPayments ?? left}` : 'Скидка использована'
  }

  const plan = promo.plan ? planLabel(promo.plan) : 'Тариф'
  return redemption.grantedUntil ? `${plan} подарен до ${formatDay(redemption.grantedUntil)}` : `${plan} подарен`
}

export const promoCodePath = (id: number): string => `/promo-codes/${id}`
